import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import AlertManager from "../../shared/Alert/Alert";
import pageClasses from "./FlaggedItems.module.css";
import layoutClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import getLandedFlaggedItems from "../../API/landedFlaggedItemsApi";
import approveLandedFlaggedItems from "../../API/landedFlaggedApproveItemsApi";
import { updateLandedCostItemWorkflow } from "../../API/landedCostUpdateApi";

const pickValue = (row, keys, fallback = "") => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const formatDateParts = (value) => {
  if (Array.isArray(value)) {
    const [year, month, day, hour, minute, second] = value;
    if (year && month && day) {
      const datePart = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (hour !== undefined) {
        return `${datePart} ${String(hour ?? 0).padStart(2, "0")}:${String(minute ?? 0).padStart(2, "0")}:${String(
          second ?? 0
        ).padStart(2, "0")}`;
      }

      return datePart;
    }
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 19).replace("T", " ");
  }

  return value ?? "";
};

const normalizeDecimalValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const parsedValue = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const EDIT_NEW_LANDED_COST_MIN = -100000;
const EDIT_NEW_LANDED_COST_MAX = 100000;

const isEditNewLandedCostWithinRange = (value) => {
  const numericValue = normalizeDecimalValue(value);
  if (numericValue === null) {
    return true;
  }

  return numericValue >= EDIT_NEW_LANDED_COST_MIN && numericValue <= EDIT_NEW_LANDED_COST_MAX;
};

const DecimalEditCell = (params) => {
  const { id, field, value } = params;
  const [inputValue, setInputValue] = useState(value ?? "");

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const handleChange = (event) => {
    const rawValue = event.target.value;
    setInputValue(rawValue);
    params.api.setEditCellValue({ id, field, value: rawValue }, event);
  };

  return (
    <input
      type="text"
      step="0.01"
      inputMode="decimal"
      min={EDIT_NEW_LANDED_COST_MIN}
      max={EDIT_NEW_LANDED_COST_MAX}
      value={inputValue}
      onChange={handleChange}
      style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "6px" }}
      data-testid={`${field}-edit-${id}`}
    />
  );
};

const normalizeRow = (row, index) => {
  const batchId = pickValue(row, ["batchId", "batch_id", "batchid"], "");
  const parsedSku = pickValue(row, ["parsedSku", "sku", "parsed_sku", "napaSku", "napa_sku"], "");

  return {
    id: pickValue(row, ["id", "rowId", "row_id", "uuid", "workflowId", "workflow_id"], `${batchId || parsedSku || "flagged"}-${index + 1}`),
    batchId,
    parsedSku,
    hqLine: pickValue(row, ["hqLine", "hq_line"], ""),
    fieldLine: pickValue(row, ["fieldLine", "field_line"], ""),
    oldLandedCost: pickValue(row, ["oldLandedCost", "old_landed_cost"], null),
    newLandedCost: pickValue(row, ["newLandedCost", "new_landed_cost"], null),
    percentChgLandedCost: pickValue(row, ["percentChgLandedCost", "percent_chg_landed_cost"], null),
    newLandedCostCopy: normalizeDecimalValue(
      pickValue(row, ["newLandedCostCopy", "new_landed_cost_copy", "newLandedCost", "new_landed_cost"], null)
    ),
    editNewLandedCost: normalizeDecimalValue(
      pickValue(
        row,
        ["editNewLandedCost", "edit_new_landed_cost", "newLandedCostCopy", "new_landed_cost_copy", "newLandedCost", "new_landed_cost"],
        null
      )
    ),
    editPercentChgLandedCost: pickValue(
      row,
      ["editPercentChgLandedCost", "edit_percent_chg_landed_cost", "percentChgLandedCost", "percent_chg_landed_cost"],
      null
    ),
    flaggedComment: pickValue(row, ["flaggedComment", "flagged_comment", "dcUserComments", "dc_user_comments", "comment", "remarks"], ""),
    flaggedBy: pickValue(row, ["dcUpdatedBy", "dc_updated_by", "flaggedBy", "flagged_by"], ""),
    asgVendorAccountNumber: pickValue(row, ["asgVendorAccountNumber", "asg_vendor_account_number"], ""),
    asgVendorName: pickValue(row, ["asgVendorName", "asg_vendor_name"], ""),
    asgVendorGroupCode: pickValue(row, ["asgVendorGroupCode", "asg_vendor_group_code"], ""),
    asgVendorProductNumber: pickValue(row, ["asgVendorProductNumber", "asg_vendor_product_number"], ""),
    asgPrimaryVendorAccountNumber: pickValue(row, ["asgPrimaryVendorAccountNumber", "asg_primary_vendor_account_number"], ""),
    productLifeCycleStateName: pickValue(row, ["productLifeCycleStateName", "product_life_cycle_state_name"], ""),
    countryOfOrigin: pickValue(row, ["countryOfOrigin", "country_of_origin"], ""),
    fobCostUnit: pickValue(row, ["fobCostUnit", "fob_cost_unit"], ""),
    unitCostDate: formatDateParts(pickValue(row, ["unitCostDate", "unit_cost_date"], "")),
    palletQuantity: pickValue(row, ["palletQuantity", "pallet_quantity"], null),
    masterCartonQuantity: pickValue(row, ["masterCartonQuantity", "master_carton_quantity"], null),
    oldFobCost: pickValue(row, ["oldFobCost", "old_fob_cost"], null),
    newFobCost: pickValue(row, ["newFobCost", "new_fob_cost"], null),
    tariffAmt: pickValue(row, ["tariffAmt", "tariff_amt"], null),
    freightAmt: pickValue(row, ["freightAmt", "freight_amt"], null),
    issCommissionAmt: pickValue(row, ["issCommissionAmt", "iss_commission_amt"], null),
    landedCostVariability: pickValue(row, ["landedCostVariability", "landed_cost_variability"], null),
    variabilityPercent: pickValue(row, ["variabilityPercent", "variability_percent"], null),
    adjustmentsRate: pickValue(row, ["adjustmentsRate", "adjustments_rate"], null),
    couterVailingRate: pickValue(row, ["couterVailingRate", "couter_vailing_rate"], null),
    adjustmentsAmt: pickValue(row, ["adjustmentsAmt", "adjustments_amt"], null),
    couterVailingAmt: pickValue(row, ["couterVailingAmt", "couter_vailing_amt"], null),
    containerPalletAmt: pickValue(row, ["containerPalletAmt", "container_pallet_amt"], null),
    poStatusValue: pickValue(row, ["poStatusValue", "po_status_value"], ""),
    poProductNumber: pickValue(row, ["poProductNumber", "po_product_number"], ""),
    areParamsMissing: pickValue(row, ["areParamsMissing", "are_params_missing", "paramMissing", "param_missing", "parammissing"], null),
    approvedDate: formatDateParts(pickValue(row, ["approvedDate", "approved_date"], "")),
    approvedBy: pickValue(row, ["approvedBy", "approved_by"], ""),
    nextLcCalcDate: formatDateParts(pickValue(row, ["nextLcCalcDate", "next_lc_calc_date"], "")),
    repricingStatus: pickValue(row, ["repricingStatus", "repricing_status"], ""),
    eventType: pickValue(row, ["eventType", "event_type"], ""),
    createDate: formatDateParts(pickValue(row, ["createDate", "create_date", "createdDate", "created_at"], "")),
    createdBy: pickValue(row, ["createdBy", "created_by"], ""),
    updateDate: formatDateParts(pickValue(row, ["updateDate", "update_date", "updatedDate", "updated_at"], "")),
    updateBy: pickValue(row, ["updateBy", "update_by"], ""),
    approvalStatus: pickValue(row, ["approvalStatus", "approval_status", "approved", "isApproved"], ""),
    lcCalculated: pickValue(row, ["lcCalculated", "lc_calculated"], ""),
    new: pickValue(row, ["new"], ""),
  };
};

const normalizeRows = (rows) => rows.map((row, index) => normalizeRow(row, index));

const ActionButtonWithTooltip = ({ title, disabled, children, buttonProps, buttonClassName }) => (
  <Tooltip title={disabled ? title : ""} disableHoverListener={!disabled} disableFocusListener={!disabled} disableTouchListener={!disabled}>
    <span style={{ display: "inline-flex" }}>
      <Button {...buttonProps} className={buttonClassName} disabled={disabled}>
        {children}
      </Button>
    </span>
  </Tooltip>
);

const FlaggedItems = () => {
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvalAction, setApprovalAction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "updateDate", sort: "desc" }]);
  const dirtyRowsRef = useRef(new Map());
  const mountedRef = useRef(true);
  const apiRef = useGridApiRef();

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    const response = await getLandedFlaggedItems();

    if (!mountedRef.current) {
      return;
    }

    setRows(Array.isArray(response) ? normalizeRows(response) : []);
    setSelectionModel([]);
    dirtyRowsRef.current.clear();
    setIsEditing(false);
    setSubmitArmed(false);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadRows();

    return () => {
      mountedRef.current = false;
    };
  }, [loadRows]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectionModel.includes(row.id)),
    [rows, selectionModel]
  );

  const selectedRowsHaveEdits = selectedRows.length > 0;
  const isApproving = approvalAction !== null;

  const handleApprovalAction = async (approvedStatus, action) => {
    if (!selectedRows.length || isEditing || saving || approvalAction) {
      return;
    }

    setApprovalAction(action);

    try {
      const response = await approveLandedFlaggedItems(selectedRows, approvedStatus);

      if (!mountedRef.current) {
        return;
      }

      if (response) {
        const message =
          approvedStatus === "Rejected"
            ? response.message || `Rejected ${selectedRows.length} flagged item(s).`
            : response.message || `Processed ${selectedRows.length} flagged item(s).`;

        AlertManager.success(message, { contactSupport: false });
        await loadRows();
      }
    } finally {
      if (mountedRef.current) {
        setApprovalAction(null);
      }
    }
  };

  const markRowAsDirty = (newRow, oldRow) => {
    if (!newRow || !oldRow) {
      return;
    }

    const changed = Object.keys(newRow).some((key) => newRow[key] !== oldRow[key]);
    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  };

  const handleProcessRowUpdate = (newRow, oldRow) => {
    if (!isEditing) {
      return oldRow;
    }

    const rawEditNewLandedCost = newRow?.editNewLandedCost;
    const normalizedEditNewLandedCost = normalizeDecimalValue(rawEditNewLandedCost);
    if (normalizedEditNewLandedCost !== null && !isEditNewLandedCostWithinRange(rawEditNewLandedCost)) {
      AlertManager.error("New landed cost must be between -100000 and 100000.", {
        contactSupport: false,
      });
      return oldRow;
    }

    const normalizedNewLandedCostCopy = normalizedEditNewLandedCost;
    const nextRow = {
      ...oldRow,
      ...newRow,
      newLandedCostCopy: normalizedNewLandedCostCopy,
      editNewLandedCost: normalizedNewLandedCostCopy,
    };

    setRows((prevRows) => prevRows.map((row) => (row.id === oldRow.id ? nextRow : row)));
    markRowAsDirty(nextRow, oldRow);
    return nextRow;
  };

  const commitActiveCellEdits = useCallback(async () => {
    const editModel = apiRef.current?.getEditRowsModel?.() || apiRef.current?.state?.editRows || {};
    const entries = Object.entries(editModel || {});

    for (const [rowId, fields] of entries) {
      const numericId = Number(rowId);
      const resolvedId = Number.isNaN(numericId) ? rowId : numericId;

      for (const field of Object.keys(fields)) {
        try {
          await Promise.resolve(apiRef.current?.commitCellChange?.({ id: resolvedId, field }));
          await Promise.resolve(
            apiRef.current?.stopCellEditMode?.({
              id: resolvedId,
              field,
              ignoreModifications: false,
            })
          );
        } catch (error) {
          console.error("Unable to commit flagged item edit", error);
        }
      }
    }
  }, [apiRef]);

  const performSave = useCallback(async () => {
    if (saving) {
      return;
    }

    const dirtyRows = Array.from(dirtyRowsRef.current.values());
    if (!dirtyRows.length) {
      setIsEditing(false);
      setSubmitArmed(false);
      return;
    }

    try {
      setSaving(true);
      const response = await updateLandedCostItemWorkflow(dirtyRows);
      if (!response) {
        return;
      }
      if (response.ok) {
        dirtyRowsRef.current.clear();
        setIsEditing(false);
        setSubmitArmed(false);
        AlertManager.success("Flagged items new landed updated successfully.", {
          contactSupport: false,
        });
        if (typeof loadRows === "function") {
          await loadRows();
        }
        return;
      }

      const errorData = await response.json().catch(() => ({}));
      AlertManager.error(errorData?.message || "Unable to update item workflow.", {
        contactSupport: false,
      });
    } catch (error) {
      AlertManager.error("Unable to update item workflow.", { contactSupport: false });
    } finally {
      setSaving(false);
    }
  }, [loadRows, saving]);

  const handleEditToggle = async () => {
    if (!isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(true);
      setSubmitArmed(false);
      return;
    }

    if (saving || !submitArmed) {
      return;
    }

    await commitActiveCellEdits();
    await performSave();
  };

  const handleFinishEditing = async () => {
    await commitActiveCellEdits();
    setSubmitArmed(true);
  };

  const columns = useMemo(
    () => [
      { field: "batchId", headerName: "Batch ID", flex: 0.9, minWidth: 90 },
      { field: "parsedSku", headerName: "Parsed SKU", flex: 0.9, minWidth: 120 },
      { field: "hqLine", headerName: "HQ Line", flex: 0.7, minWidth: 95 },
      { field: "fieldLine", headerName: "Field Line", flex: 0.8, minWidth: 105 },
      { field: "oldLandedCost", headerName: "Old landed cost", flex: 0.9, minWidth: 140 },
      { field: "newLandedCost", headerName: "New landed cost", flex: 0.9, minWidth: 140 },
      {
        field: "editNewLandedCost",
        headerName: "Modified New landed cost",
        flex: 1,
        minWidth: 160,
        editable: true,
        valueGetter: (params) => params?.row?.editNewLandedCost ?? params?.row?.newLandedCostCopy ?? params?.row?.newLandedCost ?? null,
        renderEditCell: (params) => <DecimalEditCell {...params} />,
      },
      { field: "percentChgLandedCost", headerName: "% Change Landed Cost", flex: 1, minWidth: 150 },
      { field: "editPercentChgLandedCost", headerName: "Modified % Change Landed Cost", flex: 1, minWidth: 150 },
      { field: "flaggedComment", headerName: "Flagged Comment", flex: 1.3, minWidth: 200 },
      { field: "flaggedBy", headerName: "Flagged By", flex: 0.9, minWidth: 140 },
      { field: "asgVendorAccountNumber", headerName: "ASG Vendor Account #", flex: 1.2, minWidth: 190 },
      { field: "asgVendorName", headerName: "ASG Vendor Name", flex: 1.3, minWidth: 220 },
      { field: "asgVendorGroupCode", headerName: "ASG Vendor Group Code", flex: 1, minWidth: 180 },
      { field: "asgVendorProductNumber", headerName: "ASG Vendor Product #", flex: 1.1, minWidth: 190 },
      { field: "asgPrimaryVendorAccountNumber", headerName: "ASG Primary Vendor Account #", flex: 1.4, minWidth: 230 },
      { field: "productLifeCycleStateName", headerName: "Lifecycle State", flex: 1, minWidth: 160 },
      { field: "countryOfOrigin", headerName: "Country of Origin", flex: 0.9, minWidth: 150 },
      { field: "fobCostUnit", headerName: "FOB Cost Unit", flex: 0.9, minWidth: 140 },
      { field: "unitCostDate", headerName: "Unit Cost Date", flex: 1, minWidth: 160 },
      { field: "palletQuantity", headerName: "Pallet Qty", flex: 0.8, minWidth: 110 },
      { field: "masterCartonQuantity", headerName: "Master Carton Qty", flex: 1, minWidth: 150 },
      { field: "oldFobCost", headerName: "Old FOB Cost", flex: 1, minWidth: 140 },
      { field: "newFobCost", headerName: "New FOB Cost", flex: 1, minWidth: 140 },
      { field: "tariffAmt", headerName: "Tariff Amount", flex: 1, minWidth: 140 },
      { field: "freightAmt", headerName: "Freight Amount", flex: 1, minWidth: 140 },
      { field: "issCommissionAmt", headerName: "ISS Commission Amount", flex: 1.1, minWidth: 180 },
      { field: "landedCostVariability", headerName: "Landed Cost Variability", flex: 1.2, minWidth: 200 },
      { field: "variabilityPercent", headerName: "Variability %", flex: 1, minWidth: 140 },
      { field: "adjustmentsRate", headerName: "Antidumping Rate", flex: 1, minWidth: 150 },
      { field: "couterVailingRate", headerName: "Countervailing Rate", flex: 1.2, minWidth: 180 },
      { field: "adjustmentsAmt", headerName: "Antidumping Amount", flex: 1.2, minWidth: 180 },
      { field: "couterVailingAmt", headerName: "Countervailing Amount", flex: 1.2, minWidth: 190 },
      { field: "containerPalletAmt", headerName: "Container Pallet Amount", flex: 1.3, minWidth: 210 },
      { field: "poStatusValue", headerName: "PO Status", flex: 0.9, minWidth: 140 },
      { field: "poProductNumber", headerName: "PO Product Number", flex: 1.2, minWidth: 190 },
      { field: "areParamsMissing", headerName: "Params Missing", flex: 1, minWidth: 150 },
      { field: "approvedDate", headerName: "Approved Date", flex: 1, minWidth: 160 },
      { field: "approvedBy", headerName: "Approved By", flex: 1, minWidth: 150 },
      { field: "nextLcCalcDate", headerName: "Next LC Calc Date", flex: 1.2, minWidth: 180 },
      { field: "repricingStatus", headerName: "Repricing Status", flex: 1, minWidth: 160 },
      { field: "eventType", headerName: "Event Type", flex: 0.9, minWidth: 140 },
      { field: "createDate", headerName: "Created Date", flex: 1, minWidth: 170 },
      { field: "createdBy", headerName: "Created By", flex: 1, minWidth: 140 },
      { field: "updateDate", headerName: "Updated Date", flex: 1, minWidth: 170 },
      { field: "updateBy", headerName: "Updated By", flex: 1, minWidth: 140 },
      { field: "approvalStatus", headerName: "Approved Status", flex: 0.8, minWidth: 140 },
      { field: "lcCalculated", headerName: "LC Calculated", flex: 0.9, minWidth: 140 },
      { field: "new", headerName: "New", flex: 0.6, minWidth: 100 },
    ],
    []
  );

  const CustomPagination = () => {
    const paginationApiRef = useGridApiContext();
    const page = useGridSelector(paginationApiRef, gridPageSelector);
    const pageCount = useGridSelector(paginationApiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => paginationApiRef.current.setPage(value - 1)}
      />
    );
  };

  return (
    <section className={pageClasses.page}>
      <header className={pageClasses.header}>
        <div>
          <h1>Flagged Items</h1>
          <p>Review flagged SKUs, edit the copied landed cost, and approve or reject selected items.</p>
        </div>
      </header>

      <div className={layoutClasses.resultsTableCard}>
        <Box className={layoutClasses.resultsActionBar} sx={{ justifyContent: "flex-end", width: "100%" }}>
          <ActionButtonWithTooltip
            title={isEditing && !submitArmed ? "Finish editing before saving changes." : ""}
            disabled={isLoading || saving || (isEditing && !submitArmed) || !rows.length}
            buttonProps={{
              variant: "contained",
              color: "primary",
              size: "small",
              onClick: handleEditToggle,
            }}
          >
            {saving ? "Saving..." : isEditing ? "Save Changes" : "Edit"}
          </ActionButtonWithTooltip>
          {isEditing && (
            <ActionButtonWithTooltip
              title="Finish editing before saving changes."
              disabled={saving}
              buttonProps={{ variant: "text", size: "small", onClick: handleFinishEditing }}
              buttonClassName={layoutClasses.finishEditingBtn}
            >
              Finish Editing
            </ActionButtonWithTooltip>
          )}
          <ActionButtonWithTooltip
            title={isEditing ? "Finish editing before approving or rejecting." : ""}
            disabled={isEditing || isApproving || isLoading || !selectedRowsHaveEdits}
            buttonProps={{ variant: "contained", color: "success", size: "small", onClick: () => handleApprovalAction("ReApproved", "approve") }}
          >
            {approvalAction === "approve" ? "Approving..." : "Approve"}
          </ActionButtonWithTooltip>
          <ActionButtonWithTooltip
            title={isEditing ? "Finish editing before approving or rejecting." : ""}
            disabled={isEditing || isApproving || isLoading || !selectedRowsHaveEdits}
            buttonProps={{ variant: "contained", color: "error", size: "small", onClick: () => handleApprovalAction("Rejected", "reject") }}
          >
            {approvalAction === "reject" ? "Rejecting..." : "Reject"}
          </ActionButtonWithTooltip>
        </Box>

        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns.map((column) => ({
            ...column,
            valueFormatter: (params) => formatValue(params?.value),
          }))}
          autoHeight
          pagination
          loading={isLoading || saving}
          checkboxSelection
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pageSize={15}
          rowsPerPageOptions={[15]}
          getRowId={(row) => row.id}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) => {
            const ids = Array.isArray(newSelection) ? newSelection : Array.from(newSelection || []);
            setSelectionModel(ids);
          }}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          editMode="cell"
          experimentalFeatures={{ newEditingApi: true }}
          isCellEditable={(params) => isEditing && params.field === "editNewLandedCost"}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => console.error(error)}
          components={{ Pagination: CustomPagination }}
          sx={{
            border: 0,
            fontSize: 12,
            color: "#00124c",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: "#5c6f94",
              textTransform: "uppercase",
              fontSize: 11,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9fbff",
              borderBottom: "1px solid #e0e6f0",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e6f0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(0, 20, 137, 0.04)",
            },
          }}
        />
      </div>
    </section>
  );
};

export default FlaggedItems;
